import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';

import './EditCodingForm.scss';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import {
  useDeleteCodingStamp,
  useGetEditCodingDetails,
  useGetEditCodingStamp,
  useSaveEditCodingStamp,
} from '../../../../hooks/document/useDocument';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ReactComponent as TagTreeIcon} from '../../../../assets/images/tag-tree.svg';
import {ReactComponent as DeleteIcon} from '../../../../assets/images/delete.svg';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {t} from 'i18next';
import ArrButton from '../../../common/arr-button/ArrButton';
import {CodingFormItem} from '../../../../shared/types';
import DynamicFormFields from '../../../coding-forms-management/create-edit-coding-form/preview-coding-forms/DynamicFormFields';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {ToastType} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';

export interface EditCodingStampSuccessPayload {
  stampName: string;
  sourceColorCode: string;
  stampUserId: number;
  reassignTarget?: {
    codingFormStampColorCode: string;
    codingFormStampColorLabel: string;
  };
}

interface EditCodingFormProps {
  showPanel: boolean;
  setShowPanel: React.Dispatch<React.SetStateAction<boolean>>;
  defaultCode: string;
  defaultCodingStampName: string;
  onSuccess?: (result?: EditCodingStampSuccessPayload) => void;
  codingFormId?: number | string | undefined;
  selectedCodingFormName?: string;
  onCloseDrawer?: () => void;
}
interface CodingStamp {
  codingFormUserStampID: number;
  codingFormStampColorCode: string;
  codingFormStampColorName: string;
  codingFormStampColorLabel: string;
  codingFormStampColorRGB: string;
  isDisabled: boolean;
}
const EditCodingForm: React.FC<EditCodingFormProps> = ({
  showPanel,
  setShowPanel,
  defaultCode,
  defaultCodingStampName,
  codingFormId,
  selectedCodingFormName,
  onSuccess,
  onCloseDrawer,
}) => {
  const [selectedStamp, setSelectedStamp] = useState<any>(null);
  const [defaultSelectedStampId, setDefaultSelectedStampId] =
    useState<any>(null);
  const [selectedStampIndex, setSelectedStampIndex] = useState<number | null>(
    null
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [stampList, setStampList] = useState<any[]>([]);
  const [documentCodingForm, setDocumentCodingForm] = useState<
    CodingFormItem[]
  >([]);
  const [displayStampName, setDisplayStampName] = useState<string>('');
  const [renameStampName, setRenameStampName] = useState<string>('');
  const [reassignStamp, setReassignStamp] = useState<CodingStamp | null>(null);
  const [reassignStampIndex, setReassignStampIndex] = useState<number | null>(
    null
  );
  const [isConfirmMode, setIsConfirmMode] = useState<boolean>(false);
  const [confirmType, setConfirmType] = useState<'save' | 'delete' | null>(
    null
  );
  const [initialState, setInitialState] = useState<{
    selectedStamp: any;
    displayStampName: string;
    renameStampName: string;
    reassignStamp: CodingStamp | null;
  }>({
    selectedStamp: null,
    displayStampName: '',
    renameStampName: '',
    reassignStamp: null,
  });

  const {
    mutate: getEditCodingStamp,
    data: codingStampData = [] as CodingStamp[],
  } = useGetEditCodingStamp();
  const {mutate: getEditCodingDetails, data: codingStampDetails} =
    useGetEditCodingDetails();
  const {mutate: mutateSaveEditCodingStamp} = useSaveEditCodingStamp();
  const {mutate: mutateDeleteCodingStamp} = useDeleteCodingStamp();
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const docView = useSelector((state: RootState) => state.docView);
  const selectedSourceId = useSelector(
    (state: RootState) => state.docView.sourceId
  );
  const activeDocumentId = Number(activeDocumentObj?.documentId);
  const onClose = () => {
    setShowPanel(false);
    setIsConfirmMode(false);
    setConfirmType(null);
    onCloseDrawer?.();
  };

  useEffect(() => {
    if (selectedStamp) {
      getEditCodingDetails({
        sourceID: selectedSourceId ?? 0,
        CodingFormId: Number(codingFormId),
        DocumentId: activeDocumentId,
        Code: selectedStamp.codingFormStampColorCode,
        SourceTypeCode: 'DOCUMENT',
      });
    }
  }, [selectedStamp, codingFormId]);

  useEffect(() => {
    getEditCodingStamp({
      sourceID: selectedSourceId ?? 0,
      userCodingFormID: Number(codingFormId),
    });
  }, []);

  // Initialize selectedStamp with defaultCode on mount or when codingStampData changes
  useEffect(() => {
    if (codingStampData && codingStampData.length > 0) {
      setStampList(codingStampData);
      const defaultIndex = codingStampData.findIndex(
        (stamp: any) => stamp.codingFormStampColorCode === defaultCode
      );
      if (defaultIndex !== -1) {
        const defaultStamp = codingStampData[defaultIndex];
        setSelectedStamp(defaultStamp);
        setSelectedStampIndex(defaultIndex);
        setDefaultSelectedStampId(defaultStamp?.codingFormUserStampID);
        const stampName =
          defaultStamp.codingFormStampColorName ||
          defaultStamp.codingFormStampColorLabel ||
          '';
        setDisplayStampName(stampName);
        setRenameStampName('');
        setReassignStamp(null);
        setReassignStampIndex(null);
        // Set initial state after loading data
        setInitialState({
          selectedStamp: defaultStamp,
          displayStampName: stampName,
          renameStampName: '',
          reassignStamp: null,
        });
      } else if (codingStampData.length > 0) {
        const firstStamp = codingStampData[0];
        if (firstStamp) {
          setSelectedStamp(firstStamp);
          setSelectedStampIndex(0);
          const stampName =
            firstStamp.codingFormStampColorName ||
            firstStamp.codingFormStampColorLabel ||
            '';
          setDisplayStampName(stampName);
          setRenameStampName('');
          setReassignStamp(null);
          setReassignStampIndex(null);

          // Set initial state after loading data
          setInitialState({
            selectedStamp: firstStamp,
            displayStampName: stampName,
            renameStampName: '',
            reassignStamp: null,
          });
        }
      }
    }
  }, [codingStampData, defaultCode]);

  useEffect(() => {
    if (codingStampDetails) {
      const newItems = codingStampDetails?.map(
        (control: any, index: number) => {
          let itemId = Number(control.contextID);
          let value = '';
          if (control.systemControlName) {
            const parts = control.systemControlName.split('_');
            if (parts.length > 1) {
              const parsedId = Number(parts[1]);
              if (!isNaN(parsedId)) {
                itemId = parsedId;
                value = parts[1];
              }
            }
          }
          return {
            id: control.id,
            cfItemID: index,
            sequence: control.sequence,
            type: control.contextType || '',
            specialFlag: control.isSpecial || false,
            value: value || String(itemId),
            systemLabel: control.systemControlName || '',
            friendlyLabel: control.customControlLabel || '',
            logicRuleCount: String(control.logicRuleCount || '0'),
            tagType: control.codingFormControlTypeCode || '',
            errorMessage: control.errorMessage || '',
            defaultAction: control.defaultAction || '',
            helpText: control.helpText || '',
            fieldLogic: JSON.parse(control.controlLogicJSon || '[]'),
            fieldLogicJson: control.controlLogicJSon || '',
            fieldLogicAction: control.fieldLogicAction || '',
            fieldLogicObjectLabel: `${control.customControlLabel} (${control.systemControlName})`,
            groupAssociation: control.groupControlName
              ? {
                  value: control.groupControlName,
                  label: control.groupControlName,
                }
              : null,
            metaDataType: control.metaDataType || '',
            maxLength: control.maxLength
              ? Number(control.maxLength)
              : undefined,
            minValue: control.minValue || '',
            maxValue: control.maxValue || '',
            splitLength: control.splitLength
              ? Number(control.splitLength)
              : undefined,
            dataTypeLengthCode: control.dataTypeLengthCode || '',
            isCollapsed: true,
            controlValue: control.controlValue,
            displayType: control.groupDisplayType || 'DEFAULT',
          };
        }
      );
      setDocumentCodingForm(newItems);
    }
  }, [codingStampDetails]);

  const getRenameAndReassignChanges = () => {
    const trimmedRename = renameStampName.trim();
    const renameChanged =
      Boolean(trimmedRename) &&
      trimmedRename !== initialState.displayStampName.trim();
    const reassignChanged =
      reassignStamp?.codingFormUserStampID !==
      initialState.reassignStamp?.codingFormUserStampID;

    return {renameChanged, reassignChanged};
  };

  const hasChanges = () => {
    const {renameChanged, reassignChanged} = getRenameAndReassignChanges();
    return renameChanged || reassignChanged;
  };

  const requiresReassignConfirmation = () => {
    const {reassignChanged} = getRenameAndReassignChanges();
    return Boolean(reassignChanged && reassignStamp?.isDisabled);
  };

  const isSaveDisabled = () => {
    if (!selectedStamp || !hasChanges()) {
      return true;
    }

    const {renameChanged, reassignChanged} = getRenameAndReassignChanges();
    if (reassignChanged && !renameChanged) {
      return false;
    }

    return !renameStampName.trim();
  };

  const getReassignStampDisplayLabel = (stamp: CodingStamp) => {
    if (stamp.isDisabled) {
      return (
        stamp.codingFormStampColorName || stamp.codingFormStampColorLabel || ''
      );
    }
    return t('docView.headers.codingForm.unassignedStamp');
  };

  const mapReassignDropdownItem = (stamp: CodingStamp, index: number) => {
    const displayLabel = getReassignStampDisplayLabel(stamp);
    return {
      ...stamp,
      key: `stamp-${index}`,
      value: displayLabel,
      label: displayLabel,
      id: stamp.codingFormStampColorRGB,
      name: stamp.codingFormStampColorCode,
    };
  };

  const renderReassignStampPrefix = (item: CodingStamp & {id: string}) => (
    <>
      <ArrIconButton
        key={item.codingFormStampColorLabel}
        shape="none"
        size="small"
        icon={<TagTreeIcon style={{color: `#${item.id}`}} />}
        ariaLabel="edit-stamp"
        tooltipContent={item?.codingFormStampColorName}
        testId={`reassign-stamp-${item.codingFormStampColorLabel}`}
      />
      &nbsp;
    </>
  );

  const handleReassignSelectChange = (value: any) => {
    if (value?.key) {
      const indexStr = value.key.split('-')[1];
      const index = parseInt(indexStr, 10);
      const stamp = (codingStampData || stampList)[index];
      if (stamp) {
        setReassignStamp(stamp);
        setReassignStampIndex(index);
      }
      return;
    }
    setReassignStamp(null);
    setReassignStampIndex(null);
  };

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {value, name} = event.target;

    if (name === 'codingStampName') {
      setRenameStampName(value);
    }
  };

  useEffect(() => {
    if (!codingStampData?.length && defaultCodingStampName) {
      setDisplayStampName(defaultCodingStampName);
      setRenameStampName('');
    }
  }, [defaultCodingStampName, codingStampData]);
  const triggerToast = (
    message: string,
    status: ToastType,
    successPayload?: EditCodingStampSuccessPayload
  ) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      if (status === ToastType.Success) {
        onSuccess?.(successPayload);
        onClose();
      }
    }, 1000);
  };

  const handleDeleteCodingStamp = () => {
    const saveEditStampPayload = {
      sessionObject: {
        documentListMasterID: docView.documentListMasterId,
        sourceTypeCode: docView?.sourceTypeCode || 'DOCEXPLORER',
        docsForCodeSame: [''],
        assignmentSource: '',
        sourceID: 0,
        userCodingFormID: Number(codingFormId) || 1,
        xmlCompletedDocumentEditedCodingForm: '',
      },
      colorCode: selectedStamp?.codingFormStampColorLabel,
      codingFormStampId: selectedStamp?.codingFormUserStampID,
      isDeleted: 1,
    };
    mutateDeleteCodingStamp(saveEditStampPayload, {
      onSuccess: (data) => {
        triggerToast(
          data.message ||
            t('docView.headers.codingForm.codingStampDeleteSuccess'),
          ToastType.Success
        );
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };
  const handleSaveCodingStamp = () => {
    const {reassignChanged} = getRenameAndReassignChanges();
    const stampNameToSave = renameStampName.trim() || displayStampName.trim();
    const targetStamp =
      reassignChanged && reassignStamp ? reassignStamp : selectedStamp;
    const successPayload: EditCodingStampSuccessPayload = {
      stampName: stampNameToSave,
      sourceColorCode: selectedStamp?.codingFormStampColorCode ?? '',
      stampUserId: defaultSelectedStampId,
      ...(reassignChanged && reassignStamp
        ? {
            reassignTarget: {
              codingFormStampColorCode: reassignStamp.codingFormStampColorCode,
              codingFormStampColorLabel:
                reassignStamp.codingFormStampColorLabel,
            },
          }
        : {}),
    };

    const saveEditStampPayload = {
      sessionObject: {
        documentListMasterID: docView.documentListMasterId,
        sourceTypeCode: docView?.sourceTypeCode || 'DOCEXPLORER',
        docsForCodeSame: [''],
        assignmentSource: '',
        sourceID: selectedSourceId ?? 0,
        userCodingFormID: Number(codingFormId),
        xmlCompletedDocumentEditedCodingForm: '',
      },
      colorCode: targetStamp?.codingFormStampColorLabel,
      codingFormStampId: defaultSelectedStampId,
      isDeleted: 0,
      codingStampName: stampNameToSave,
      codingFormUserStampName: stampNameToSave,
    };
    mutateSaveEditCodingStamp(saveEditStampPayload, {
      onSuccess: (data) => {
        triggerToast(
          data.message ||
            t('docView.headers.codingForm.codingStampSavedSucces'),
          ToastType.Success,
          successPayload
        );
        setDisplayStampName(stampNameToSave);
        setInitialState({
          selectedStamp,
          displayStampName: stampNameToSave,
          renameStampName: '',
          reassignStamp,
        });
        setRenameStampName('');
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };
  const handleSaveClick = () => {
    if (!requiresReassignConfirmation()) {
      handleSaveCodingStamp();
      return;
    }

    openConfirm('save');
  };

  const openConfirm = (type: 'save' | 'delete') => {
    setConfirmType(type);
    setIsConfirmMode(true);
  };

  const handleConfirmNo = () => {
    setIsConfirmMode(false);
    setConfirmType(null);
  };

  const handleConfirmYes = () => {
    if (confirmType === 'save') {
      handleSaveCodingStamp();
    } else if (confirmType === 'delete') {
      handleDeleteCodingStamp();
    }
    setIsConfirmMode(false);
    setConfirmType(null);
  };

  const DrawerHeader = () => (
    <Box className="edit-coding-form-header">
      <Box className="title-box">
        <Typography
          className="typo-text headline3_size title"
          data-testid="edit-coding-stamp-title"
        >
          {t('docView.headers.codingForm.editCodingStamp')}
        </Typography>
      </Box>
      {!isConfirmMode ? (
        <Box className="action-buttons-container">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              onClose();
            }}
            disabled={false}
            dataTestId="edit-coding-form-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            onClick={handleSaveClick}
            disabled={isSaveDisabled()}
            dataTestId="edit-coding-stamp-save-btn"
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      ) : null}
    </Box>
  );

  const renderStampInfoRow = () => (
    <Box
      className="edit-coding-form-stamp-info"
      data-testid="edit-coding-form-stamp-info"
    >
      <TagTreeIcon
        className="edit-coding-form-stamp-icon"
        style={{
          color: selectedStamp?.codingFormStampColorRGB
            ? `#${selectedStamp.codingFormStampColorRGB}`
            : undefined,
        }}
        data-testid="edit-coding-form-stamp-icon"
      />
      <Typography
        className="typo-text body2_size edit-coding-form-stamp-name"
        data-testid="edit-coding-form-stamp-name"
      >
        {t('docView.headers.codingForm.stampNameDisplay', {
          stampName: displayStampName,
        })}
      </Typography>
      <ArrButton
        variant="secondary"
        size="medium"
        aria-label={t('common.delete')}
        dataTestId="delete-button"
        startIcon={<DeleteIcon />}
        disabled={!selectedStamp?.isDisabled}
        className="edit-coding-form-delete-btn"
        onClick={() => openConfirm('delete')}
      >
        {t('docView.headers.codingForm.deleteStamp')}
      </ArrButton>
    </Box>
  );

  const renderRenameAndReassignRow = () => (
    <Box
      className="edit-coding-form-rename-reassign-row"
      data-testid="edit-coding-form-rename-reassign-row"
    >
      <Box className="edit-coding-form-rename-field">
        <ArrTextField
          id="coding-stamp-name-input"
          className="edit-coding-form-rename-textfield"
          hiddenLabel
          placeholder={t('docView.headers.codingForm.renameStampPlaceholder')}
          name="codingStampName"
          aria-label={t('docView.headers.codingForm.renameStampPlaceholder')}
          value={renameStampName}
          onChange={handleTextInputChange}
          dataTestId="edit-coding-first-name"
        />
      </Box>
      <Box className="edit-coding-form-reassign-dropdown">
        <ArrSingleSelectDropdown
          dropdownLabel={t('docView.headers.codingForm.reassignThisStamp')}
          dropdownItems={(codingStampData || stampList)?.map((stamp, index) =>
            mapReassignDropdownItem(stamp, index)
          )}
          selectedValues={
            reassignStamp && reassignStampIndex !== null
              ? mapReassignDropdownItem(reassignStamp, reassignStampIndex)
              : null
          }
          dropdownItemKey="key"
          dropdownItemDisplay="value"
          formControlRestProps={{}}
          onSelectionChange={handleReassignSelectChange}
          dataTestId="edit-coding-form-reassign-stamp"
          renderPrefix={renderReassignStampPrefix}
          renderSelectedPrefix={(item: any) => (
            <ArrIconButton
              key={item?.codingFormStampColorLabel}
              shape="none"
              size="small"
              icon={<TagTreeIcon style={{color: `#${item?.id}`}} />}
              ariaLabel="edit-stamp"
              tooltipContent={item?.codingFormStampColorName}
              testId="reassign-stamp-selected"
            />
          )}
        />
      </Box>
    </Box>
  );

  const renderConfirmationContent = () => (
    <Box className="edit-coding-form-confirmation">
      <Box className="confirmation-content">
        <Box
          className="confirmation-stamp-row"
          data-testid="confirmation-stamp-row"
        >
          <ArrIconButton
            key="confirmation"
            shape="none"
            size="small"
            className="confirmation-stamp-icon-button"
            icon={
              <TagTreeIcon
                style={{
                  color: `#${confirmType == 'save' ? reassignStamp?.codingFormStampColorRGB : selectedStamp?.codingFormStampColorRGB}`,
                }}
              />
            }
            ariaLabel="confirmation-stamp"
            tooltipContent=""
            testId="confirmation-stamp"
          />
          <Typography
            className="typo-text body2_size confirmation-stamp-name"
            data-testid="confirmation-stamp-name"
          >
            {t('docView.headers.codingForm.stampNameDisplay', {
              stampName:
                confirmType == 'save'
                  ? renameStampName === ''
                    ? reassignStamp?.codingFormStampColorName
                    : renameStampName
                  : displayStampName,
            })}
          </Typography>
        </Box>
        <Typography
          className="typo-text body2_size confirmation-text"
          data-testid="confirmation-text"
        >
          {t('docView.headers.codingForm.inUse')}{' '}
          <br data-testid="edit-coding-form-br" />
          {t('docView.headers.codingForm.questionGeneric', {
            action: t(
              confirmType === 'save'
                ? t('docView.headers.codingForm.overwrite').toLowerCase()
                : t('common.delete').toLowerCase()
            ),
          })}
        </Typography>
      </Box>
      <Box className="confirmation-buttons">
        <ArrButton
          variant={confirmType === 'save' ? 'primary' : 'danger'}
          size="medium"
          onClick={handleConfirmYes}
          dataTestId="confirm-yes"
        >
          {t('docView.headers.codingForm.yesWithAction', {
            action: t(
              confirmType === 'save'
                ? t('docView.headers.codingForm.overwrite')
                : t('common.delete')
            ),
          })}
        </ArrButton>
        <ArrButton
          variant="secondary"
          size="medium"
          onClick={handleConfirmNo}
          dataTestId="confirm-no"
        >
          {t('common.no')}
        </ArrButton>
      </Box>
    </Box>
  );

  const drawerContents = (
    <>
      <Box className="edit-coding-form-Container">
        <DrawerHeader />
        {!isConfirmMode ? (
          <>
            <Box className="edit-coding-form-stamp-info-section">
              {renderStampInfoRow()}
              <Box
                className="edit-coding-form-stamp-divider"
                data-testid="edit-coding-form-stamp-divider"
                aria-hidden="true"
              />
            </Box>
            <Box className="edit-coding-form-rename-reassign-section">
              {renderRenameAndReassignRow()}
            </Box>
          </>
        ) : null}
        <Box className="edit-coding-form-content">
          {!isConfirmMode ? null : renderConfirmationContent()}
          {!isConfirmMode ? (
            <Box className="dynamic-form-fields-container">
              <Box className="section-title-box">
                <Typography
                  className="typo-text headline5_size"
                  data-testid="coding-form-fields-title"
                >
                  {selectedCodingFormName}
                </Typography>
              </Box>
              <Box className="dynamic-form-fields-box">
                <DynamicFormFields
                  fields={documentCodingForm}
                  setMessageType={() => {}}
                  showTestButton={false}
                  disableAll={true}
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </>
  );

  return (
    <Box>
      <ArrDrawer
        isOpen={showPanel}
        showHeader={false}
        showButton={false}
        ariaLabel="drawer content"
        size="small"
        side="right"
        onClose={() => onClose()}
      >
        {drawerContents}
      </ArrDrawer>
    </Box>
  );
};

export default EditCodingForm;
